import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Signal } from '@verlo/shared/components/ui/signal';

/**
 * The back bar (FOH UX review, wave 1). Any screen that isn't one of the five nav tabs
 * starts with this: where you came from, what you're on, its one state word, its actions.
 *
 * "Where you came from" is real: a screen that sends someone here passes
 * `state: { from: { path, label } }` (see `useOrigin`), and Back returns them to exactly
 * that — the floor plan, not the list; the same date on Bookings; the Orders board. With no
 * origin, `fallback` is the sensible parent.
 *
 *   <BackBar title="T3" state={{ tone: 'warn', word: 'mains · 47m' }} fallback={{ path: '/tables', label: 'Tables' }}>
 *     <Button>Bill</Button>
 *   </BackBar>
 */
export function useOrigin(fallback) {
  const location = useLocation();
  const from = location.state?.from;
  if (from && from.path) return from;
  // No origin was passed, but this tab has in-app history (react-router keeps an index in
  // history.state): "Back" means the browser's back, which is where the user actually was.
  const idx = typeof window !== 'undefined' ? window.history.state?.idx : 0;
  if (idx > 0) return { path: -1, label: 'Back' };
  return fallback;
}

/** The `state` to pass with `navigate()` so the next screen's Back knows the way home. */
export const origin = (path, label) => ({ from: { path, label } });

// `compact` (the order page on a 4:3 till): one 48px row that never wraps, and the children
// fill the rest of it rather than sitting at the far end, so a row of chips can shrink.
const BackBar = ({ title, subtitle, state, fallback = { path: '/tables', label: 'Tables' }, children, onBack, testId = 'back-bar', compact = false }) => {
  const navigate = useNavigate();
  const from = useOrigin(fallback);
  const back = () => (onBack ? onBack(from) : navigate(from.path));
  // `from.path` may be -1 (the browser's back); `navigate` accepts either.
  return (
    <div className={compact
      ? 'flex h-12 flex-nowrap items-center gap-2 border border-hairline bg-white pl-1 pr-1.5'
      : 'flex flex-wrap items-center gap-x-3 gap-y-2 border border-hairline bg-white px-3 py-2'} data-testid={testId}>
      <button
        type="button"
        onClick={back}
        className="inline-flex h-10 shrink-0 items-center gap-1 whitespace-nowrap pr-3 pl-1 font-medium text-verlo-hover hover:bg-verlo-tint"
        data-testid="back-to-tables-btn"
      >
        <ChevronLeft size={20} />
        <span className="text-[14px]">{from.label}</span>
      </button>
      <span className="h-6 w-px shrink-0 bg-hairline" aria-hidden />
      <div className="flex min-w-0 shrink-0 items-baseline gap-2">
        <h1 className={`font-heading ${compact ? 'text-[20px]' : 'text-[22px]'} font-extrabold leading-none text-ink`}>{title}</h1>
        {subtitle && <span className="truncate text-[13px] text-ink-soft">{subtitle}</span>}
      </div>
      {state?.word && <Signal tone={state.tone || 'info'} className="shrink-0">{state.word}</Signal>}
      <div className={compact ? 'flex min-w-0 flex-1 items-center gap-1.5' : 'ml-auto flex shrink-0 items-center gap-2'}>{children}</div>
    </div>
  );
};

export default BackBar;
